import { useState } from "react";
import { BiSolidShareAlt } from "react-icons/bi";
import { IoClose } from "react-icons/io5";

import { cn } from "@bentolabs/kit/utils";
import { useCopyToClipboard } from "usehooks-ts";

import { useBuildCommandMutation } from "shared/api/use-build-command-mutation";
import Share from "shared/assets/share.svg?react";
import { Button } from "shared/ui/button";
import { Dialog } from "shared/ui/dialog";

import { Input } from "./input";

type Props = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

type Step = "create-link" | "link-created";

export const CreateRefLinkDialog = (props: Props) => {
  const { isOpen, onOpenChange } = props;

  const [step, setStep] = useState<Step>("create-link");
  const [inviteCode, setInviteCode] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [isError, setIsError] = useState(false);

  const [_, copy] = useCopyToClipboard();

  const buildCommandMutation = useBuildCommandMutation();

  const handleBuildCommand = () => {
    buildCommandMutation.mutate(
      {
        name: "set-invite-code",
        args: { inviteCode },
      },
      {
        onSuccess: () => {
          setStep("link-created");
        },
      }
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content className="md:max-w-sm">
        {step === "create-link" && (
          <div className="my-auto">
            <Dialog.Body className="mb-0 flex flex-col items-center gap-0 px-6 py-3.5">
              <Dialog.Close asChild className="ml-auto">
                <Button variant="nobel/10" className="size-9.5 p-0">
                  <IoClose className="size-6" />
                </Button>
              </Dialog.Close>
              <div className="mt-5 inline-flex size-12.5 items-center justify-center rounded-xl bg-black">
                <Share className="size-9" />
              </div>
              <Dialog.Title className="mt-5 p-0 text-[28px] leading-[105%]">
                Your Referral Link
              </Dialog.Title>
              <Dialog.Description className="text-nobel/70 mt-4 flex leading-[150%]">
                Your custom link is created once and is yours to keep - no need
                to generate a new link for every new user you want to refer.
              </Dialog.Description>

              <Input
                value={inviteCode}
                onChange={(e) => {
                  const val = e.target.value.trim();
                  setIsError(!/^[A-Za-z0-9]*$/.test(val));
                  setInviteCode(val);
                }}
                className="mt-7.5"
                placeholder="your custom name"
              />
              <span
                className={cn(
                  "text-nobel/70 mt-2.5 flex self-start text-sm leading-[150%] font-normal",
                  isError && "text-pink-lemonade"
                )}
              >
                Alphabetic or numerical symbols (A-Z, a-z, 0-9)
              </span>

              <Button
                disabled={!inviteCode || isError}
                isLoading={buildCommandMutation.isPending}
                variant="spearmint"
                className="mt-7.5 w-full"
                onClick={handleBuildCommand}
              >
                Create and copy link
              </Button>
            </Dialog.Body>
            <Dialog.Body className="text-nobel/70 mt-1.5 mb-0 p-3.5">
              If you shared referral links previously, they will continue to
              work after the creation of a new personal link.
            </Dialog.Body>
          </div>
        )}

        {step === "link-created" && (
          <Dialog.Body className="flex flex-col items-center gap-0 px-6 py-3.5">
            <Dialog.Close asChild className="ml-auto">
              <Button variant="nobel/10" className="size-9.5 p-0">
                <IoClose className="size-6" />
              </Button>
            </Dialog.Close>
            <div
              style={{
                boxShadow: `0px -4.1px 4.73px 0px #33FF00 inset, 0px -4.58px 3.18px 0px #33FF00 inset, 0px 0.37px 8.31px 0px #22ED4E40`,
                backgroundImage: `radial-gradient(55.5% 56.44% at 57.52% 45.81%, #11F5A5 0%, #1DC942 100%)`,
              }}
              className="mt-5 inline-flex size-12.5 items-center justify-center rounded-xl text-black"
            >
              <BiSolidShareAlt className="size-9" />
            </div>
            <Dialog.Title className="mt-5 p-0 text-[28px] leading-[105%]">
              Link Created
            </Dialog.Title>

            <Input
              type="button"
              value={`${window.location.origin}/ref/${inviteCode}`}
              onClick={() => {
                copy(`${window.location.origin}/ref/${inviteCode}`);
              }}
              className="mt-10 cursor-pointer text-center"
            />

            <Button
              variant="spearmint"
              className="mt-3 w-full"
              onClick={() => {
                copy(`${window.location.origin}/ref/${inviteCode}`);
                setIsCopied(true);
                setTimeout(() => {
                  setIsCopied(false);
                }, 3000);
              }}
            >
              {isCopied ? "Copied" : "Copy link"}
            </Button>
          </Dialog.Body>
        )}
      </Dialog.Content>
    </Dialog>
  );
};
